import { Text } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/material";
import { useCreateAccountReactivationRequest } from "@src/appV2/AccountReactivation";
import { useWorkerLoggedIn } from "@src/appV2/Agents/api/useWorkerLoggedIn";
import { useLegacyStateContext } from "@src/appV2/LegacyStateBridge/context";
import {
  APP_V2_USER_EVENTS,
  extractPhoneNumberDigits,
  ProgressDialog,
  useToast,
} from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { MuiThemeProvider } from "@src/appV2/lib/Theme";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase";
import { useMutation } from "@tanstack/react-query";
import { type ReactElement, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useHistory } from "react-router-dom";

import { Button } from "../../../redesign/components/Button";
import {
  LoginConfirmationDialog,
  PhoneNumberExistsDialog,
  SignUpInsteadDialog,
  VerifyOtpDialog,
} from "../Dialogs";
import { ReactivationRequiredDialog } from "../Dialogs/ReactivationRequiredDialog";
import { useAutoLoginFromUrl } from "../hooks/useAutoLoginFromUrl";
import { useLoginSignUpUiConfiguration } from "../hooks/useLoginSignUpUiConfiguration";
import { AuthFormContent, AuthMode, useAuthMode } from "../libs";
import { useAuthWorkflow } from "../libs/authUtils";
import { DialogAuthMethod } from "../types";
import { AuthMethod, type LoginSignUpFormFields } from "../types";
import { AuthFormFields } from "./FormFields";
import { getFormSchema } from "./schema";

export function LoginSignUpForm(): ReactElement {
  const {
    initiateLogin,
    initiateSignUp,
    resendOtp,
    otpRequestIsLoading,
    loginLinkRequestIsLoading,
    signUpInsteadPhoneModalState,
    signUpInsteadEmailModalState,
    otpModalState,
    emailConfirmationModalState,
    authLoadingMessage,
    phoneNumberExistsModalState,
    reactivationModalState,
    authMethod,
    setAuthMethod,
    identityProvider,
    cognitoIsSignup,
  } = useAuthWorkflow();

  const history = useHistory();

  const {
    mutateAsync: createAccountReactivationRequest,
    isLoading: isCreatingAccountReactivationRequest,
  } = useCreateAccountReactivationRequest();

  const { hideFooterAndSecondaryButtonsOnKeyboardOpen } = useLoginSignUpUiConfiguration();

  const isKeyboardOpen = useIsKeyboardOpen();

  const shouldShowFooterAndSecondaryButtons = hideFooterAndSecondaryButtonsOnKeyboardOpen
    ? !isKeyboardOpen
    : true;

  const searchParams = useSearchParams<{ phoneNumber?: string }>();
  const phoneNumberFromUrl = searchParams.phoneNumber;

  const { authMode, authFormContent, authModeIsLogin } = useAuthMode();
  const formMethods = useForm<LoginSignUpFormFields>({
    defaultValues: {
      phoneNumber: phoneNumberFromUrl ?? "",
      email: "",
    },
    resolver: zodResolver(getFormSchema(authMode, authMethod)),
  });
  const { formState, handleSubmit, watch, setError, clearErrors, reset, setValue } = formMethods;

  const phoneNumber = watch("phoneNumber");
  const email = watch("email");
  const { showErrorToast, showInfoToast } = useToast();

  const handleMaxLengthAttempt = useCallback(() => {
    setError("phoneNumber", {
      type: "manual",
      message: "Phone number cannot be more than 10 digits.",
    });
  }, [setError]);

  const { mutateAsync: sendAgentLoggedIn } = useWorkerLoggedIn();
  const {
    mutateAsync: onAuthenticateWithToken,
    isLoading: authenticationIsLoading,
    isSuccess: authenticationIsSuccessful,
  } = useMutation({
    mutationFn: async ({ token }: { token: string }) => {
      /**
       * Rely on appV1 firebase event listener to update auth state and redirect.
       * `getFirebaseSingleton().onAuthStateChanged` and `getFirebaseSingleton().onIdTokenChanged`
       * in v1 init code.
       */
      await getFirebaseSingleton().signInWithCustomToken(token);
    },
    onSuccess: async () => {
      if (authMode === AuthMode.LOGIN) {
        try {
          /**
           * In authV1, we fire (without async) a post to notify the server that we
           * are logged in. Ignore errors since this is just for profiling.
           */
          await sendAgentLoggedIn();
        } catch (error) {
          logError(APP_V2_APP_EVENTS.AGENT_PROFILE_LOGGED_IN_ERROR, { error });
        }
      }
    },
    onError: (error) => {
      showErrorToast("Failed to authenticate, please try again.");
      logError(APP_V2_APP_EVENTS.FIREBASE_OTP_SIGN_IN_ERROR, { error });
    },
  });

  const {
    session: { isAuthorized },
    setLegacyIsSignUp,
  } = useLegacyStateContext();

  useAutoLoginFromUrl({
    phoneNumberFromUrl,
    authMethod,
    authMode,
    authModeIsLogin,
    formState,
    formMethods,
    setAuthMethod,
    setLegacyIsSignUp,
    initiateLogin,
  });

  const handleNavigateToSignUp = (params?: { phoneNumberToUse?: string }) => {
    const { phoneNumberToUse } = params ?? {};
    setAuthMethod(AuthMethod.PHONE);
    setValue("phoneNumber", phoneNumberToUse ?? "");
    setValue("email", "");
    clearErrors();
    history.push(AuthMode.SIGN_UP);
    logEvent(APP_V2_USER_EVENTS.SELECT_SIGN_UP_INSTEAD);
  };

  const handleClearForm = () => {
    history.push(AuthMode.LOGIN);
    reset({
      phoneNumber: "",
      email: "",
    });
    clearErrors();
    logEvent(APP_V2_USER_EVENTS.CLOSE_SIGN_UP_INSTEAD_DIALOG);
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
        onSubmit={handleSubmit(async (formData) => {
          try {
            setLegacyIsSignUp(authMode);
            switch (authMode) {
              case AuthMode.LOGIN: {
                if (authMethod === AuthMethod.PHONE) {
                  await initiateLogin({
                    phoneNumber: formData.phoneNumber,
                    email: "",
                  });

                  logEvent(APP_V2_USER_EVENTS.SUBMIT_LOGIN_FORM_WITH_PHONE_NUMBER, {
                    phoneNumber: formData.phoneNumber,
                  });
                } else {
                  await initiateLogin({
                    phoneNumber: "",
                    email: formData.email,
                  });
                  logEvent(APP_V2_USER_EVENTS.SUBMIT_LOGIN_FORM_WITH_EMAIL, {
                    email: formData.email,
                  });
                }

                return true;
              }

              case AuthMode.SIGN_UP: {
                await initiateSignUp(formData);
                logEvent(APP_V2_USER_EVENTS.SUBMIT_SIGNUP_FORM, {
                  phoneNumber: formData.phoneNumber,
                  email: formData.email,
                });
                return true;
              }

              default: {
                throw new Error(`Unknown AuthMode: ${authMode as string}`);
              }
            }
          } catch (error) {
            showErrorToast("Failed to log in.");
            logError(APP_V2_APP_EVENTS.AUTH_ON_SUBMIT_FAILURE, { error });
            return false;
          }
        })}
      >
        <Stack
          direction="column"
          spacing={6}
          justifyContent={authModeIsLogin ? "space-between" : "flex-start"}
          flex={1}
        >
          <Stack direction="column" spacing={6}>
            <AuthFormFields authMethod={authMethod} onMaxLengthAttempt={handleMaxLengthAttempt} />
            <Stack direction="column" spacing={4}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={formState.isSubmitting}
                size="large"
              >
                {authFormContent[AuthFormContent.SUBMIT_BUTTON]}
              </Button>
              {authModeIsLogin && shouldShowFooterAndSecondaryButtons ? (
                <Stack alignItems="center">
                  <TextButton
                    size="large"
                    onClick={() => {
                      const newAuthMethod =
                        authMethod === AuthMethod.PHONE ? AuthMethod.EMAIL : AuthMethod.PHONE;

                      logEvent(APP_V2_USER_EVENTS.SWITCH_LOGIN_METHOD, {
                        from: authMethod,
                        to: newAuthMethod,
                      });

                      setAuthMethod(newAuthMethod);
                    }}
                  >
                    <Text semibold sx={(theme) => ({ color: theme.palette.link?.primary })}>
                      {authMethod === AuthMethod.PHONE
                        ? "Use email to log in instead"
                        : "Use phone number to log in instead"}
                    </Text>
                  </TextButton>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
          {shouldShowFooterAndSecondaryButtons && (
            <Text
              semibold
              textAlign="center"
              sx={{ color: (theme) => theme.palette.text.secondary }}
              pb={authModeIsLogin ? 6 : 0}
            >
              {authFormContent[AuthFormContent.SWITCH_AUTH_MODE_INSTRUCTIONS]}{" "}
              <Link
                to={{
                  pathname: authMode === AuthMode.LOGIN ? AuthMode.SIGN_UP : AuthMode.LOGIN,
                  state: history.location.state,
                }}
                onClick={() => {
                  // Reset to login by phone number when interchanging between Login/Sign Up
                  setAuthMethod(AuthMethod.PHONE);

                  clearErrors();
                  logEvent(APP_V2_USER_EVENTS.SWITCH_BETWEEN_LOGIN_SIGNUP, {
                    from: authMode,
                    to: authMode === AuthMode.LOGIN ? AuthMode.SIGN_UP : AuthMode.LOGIN,
                  });
                }}
              >
                <Text
                  semibold
                  sx={(theme) => ({ color: theme.palette.link?.primary, display: "inline" })}
                >
                  {authFormContent[AuthFormContent.SWITCH_AUTH_MODE_CTA]}
                </Text>
              </Link>
            </Text>
          )}
        </Stack>
      </Box>

      <MuiThemeProvider>
        <SignUpInsteadDialog
          modalState={signUpInsteadPhoneModalState}
          authMethod={DialogAuthMethod.PHONE}
          userInput={phoneNumber}
          onClearForm={() => {
            handleClearForm();
            signUpInsteadPhoneModalState.closeModal();
          }}
          onNavigateToSignUp={() => {
            handleNavigateToSignUp({ phoneNumberToUse: phoneNumber });
            signUpInsteadPhoneModalState.closeModal();
          }}
        />
        <SignUpInsteadDialog
          modalState={signUpInsteadEmailModalState}
          authMethod={DialogAuthMethod.EMAIL}
          userInput={email}
          onClearForm={() => {
            handleClearForm();
            signUpInsteadEmailModalState.closeModal();
          }}
          onNavigateToSignUp={() => {
            handleNavigateToSignUp({ phoneNumberToUse: "" });
            signUpInsteadEmailModalState.closeModal();
          }}
        />

        <VerifyOtpDialog
          modalState={otpModalState}
          phoneNumber={phoneNumber}
          authMode={authMode}
          identityProvider={identityProvider}
          cognitoIsSignup={cognitoIsSignup}
          resendOtp={resendOtp}
          isAuthenticating={
            authenticationIsLoading ||
            /**
             * In addition to loading, we also need to show loading progress
             * for the firebase auth listeners, until we are authorized.
             */
            (authenticationIsSuccessful && !isAuthorized)
          }
          onSuccess={async (params) => {
            const { firebaseToken, agentDeletionRequestCancelled } = params;

            /**
             * For Cognito signups, firebaseToken is empty because the user doesn't exist
             * in the CBH backend yet. The user only exists in Cognito at this point.
             *
             * Close the OTP modal and navigate to onboarding where the backend user
             * will be created during the onboarding flow.
             */
            if (!firebaseToken) {
              otpModalState.closeModal();
              history.push("/home/onboarding/agentEmail");
              logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM_SUCCESS);
              return;
            }

            await onAuthenticateWithToken({ token: firebaseToken });

            if (agentDeletionRequestCancelled) {
              showInfoToast(
                "Your deletion request has been cancelled because you logged in. If you still wish to delete your account, please submit a new deletion request from the Profile page."
              );
            }

            logEvent(APP_V2_USER_EVENTS.SUBMIT_AUTH_OTP_FORM_SUCCESS);
          }}
        />
        <LoginConfirmationDialog modalState={emailConfirmationModalState} email={email} />

        <PhoneNumberExistsDialog
          modalState={phoneNumberExistsModalState}
          phoneNumber={phoneNumber}
          onLogin={() => {
            void initiateLogin({ phoneNumber, email: "" });
          }}
          onUseDifferentPhoneNumber={() => {
            reset({
              phoneNumber: "",
              email,
            });
          }}
        />

        <ReactivationRequiredDialog
          modalState={reactivationModalState}
          isLoading={isCreatingAccountReactivationRequest}
          onConfirm={async () => {
            try {
              const phoneNumberDigits = extractPhoneNumberDigits(phoneNumber);
              const { data } = await createAccountReactivationRequest(
                authMethod === AuthMethod.EMAIL ? { email } : { phone: phoneNumberDigits }
              );

              if (authMethod === AuthMethod.EMAIL) {
                const params = new URLSearchParams({
                  reactivationId: data.id,
                  email,
                  nextPath: `/v2/reactivation/phone?reactivationId=${data.id}`,
                });

                history.push(`/v2/reactivation/email?${params.toString()}`);
              } else {
                const params = new URLSearchParams({
                  reactivationId: data.id,
                  phoneNumber,
                  nextPath: `/v2/reactivation/email?reactivationId=${data.id}`,
                });

                history.push(`/v2/reactivation/phone?${params.toString()}`);
              }
            } catch {
              showErrorToast(
                "Something went wrong during account reactivation. Please contact support."
              );
            }
          }}
        />

        <ProgressDialog
          modalIsOpen={otpRequestIsLoading || loginLinkRequestIsLoading}
          messageContent={authLoadingMessage}
        />
      </MuiThemeProvider>
    </FormProvider>
  );
}
